/**
 * Centralized Slot Prop Normalizers
 *
 * Maps raw slot payload (from AEM Content Fragments or Adobe Target/Alloy DCA)
 * into component-specific props. Each component has its own normalizer that
 * handles both CF and DCA data structures. Each normalizer derives what it
 * needs from the combined slot and trackingCtx.
 */
import type { renditions, TrackingPropsInterface } from '@marriott/mi-ui-library';
import type { ProductDetailsCardProps } from '../organisms/ProductDetailsCard/ProductDetailsCard.types';
import type { EnrollmentCardContainerProps } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainer.types';
import type { SlotContent, SlotMeta } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { pickLayeredFeaturePropsConfiguration } from './adapters';
import { buildDynamicMediaNode, ASPECT_RATIOS } from './dynamicMediaUtils';
import { getBaseUrlAndAltFromPayload } from './imagePayloadUtils';
import { buildCardTrackingDescription, resolveCardCategory, resolveCardDescription } from './polarisTrackingUtils';

export type SlotTrackingContext = {
  index: number;
  tabIndex: number;
  tabName: string;
  slotSize: 'square' | 'tall' | 'banner';
  trackingProperties?: TrackingPropsInterface;
};

export type SlotResolveContext = {
  slot: SlotContent;
  trackingCtx?: SlotTrackingContext;
};

const EMPTY_RENDITIONS: renditions = { altText: '', assetPath: '', dynamic: false, renditions: [] };

function getContentObj(slot: SlotContent): Record<string, unknown> {
  return (slot?.payload && typeof slot?.payload === 'object' ? slot.payload : {}) as Record<string, unknown>;
}

function trackingProps(
  c: Record<string, unknown>,
  trackingCtx?: SlotTrackingContext,
  slotType?: 'cf' | 'dca' | string
): TrackingPropsInterface {
  const base = (trackingCtx?.trackingProperties ?? {}) as Record<string, unknown>;
  const category = resolveCardCategory(c, slotType);
  const description = resolveCardDescription(c, slotType);
  const cardTrackingDesc = buildCardTrackingDescription({
    tabIndex: trackingCtx?.tabIndex ?? 1,
    tabName: trackingCtx?.tabName ?? '',
    cardNumber: trackingCtx?.index ?? 1,
    shape: trackingCtx?.slotSize ?? 'square',
    category,
    description,
  });
  return {
    ...base,
    trackingContentPosition: base['trackingContentPosition'] || (c['trackingContentPosition'] as string),
    trackingDescription: cardTrackingDesc,
    clickTrack: c['clickTrack'] ?? base['clickTrack'],
    impressionTrack: c['impressionTrack'] ?? base['impressionTrack'],
    enableScrollingBehavior: c['enableScrollingBehavior'] ?? base['enableScrollingBehavior'],
  } as TrackingPropsInterface;
}

// ─── ProductDetailsCard ────────────────────────────────────────────────────
export function normalizeProductDetailsCardProps(ctx: SlotResolveContext): ProductDetailsCardProps {
  const c = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(c);
  const size = (ctx.slot?.size as SlotMeta['size']) ?? 'square';
  const idx = ctx.trackingCtx?.index ?? 0;
  const base = ctx.trackingCtx?.trackingProperties ?? {};
  const category = resolveCardCategory(c, ctx.slot?.type);
  const cardDesc = resolveCardDescription(c, ctx.slot?.type);
  const cardTrackingDesc = buildCardTrackingDescription({
    tabIndex: ctx.trackingCtx?.tabIndex ?? 1,
    tabName: ctx.trackingCtx?.tabName ?? '',
    cardNumber: idx,
    shape: ctx.trackingCtx?.slotSize ?? size,
    category,
    description: cardDesc,
  });
  return {
    ...c,
    title: (c['title'] || c['header'] || c['headerLong'] || '') as string,
    eyebrow: (c['eyebrow'] || c['eyebrowtext'] || '') as string,
    description: (c['description'] ||
      c['descriptionLong'] ||
      c['descriptionMedium'] ||
      c['descriptionShort'] ||
      '') as string,
    fileReferenceImage: baseUrl,
    breakPointKey: 'viewportL',
    cardVariation: size,
    index: idx,
    trackingProperties: {
      ...base,
      trackingDescription: cardTrackingDesc,
    } as TrackingPropsInterface,
    cardUrl: c['cardUrl'] || '#',
    openInaNewTab: c['openInaNewTab'] === true,
    imageAltText: c['imageAltText'] || alt || '',
    contentType: c['contentType'],
    dynamicMedia: buildDynamicMediaNode(baseUrl, ASPECT_RATIOS.SQUARE, alt) ?? EMPTY_RENDITIONS,
    dynamicMediaWide: buildDynamicMediaNode(baseUrl, ASPECT_RATIOS.WIDE_HOR, alt) ?? EMPTY_RENDITIONS,
    dynamicMediaTall: buildDynamicMediaNode(baseUrl, ASPECT_RATIOS.CLASSIC_VER, alt) ?? EMPTY_RENDITIONS,
  } as ProductDetailsCardProps;
}

// ─── EnrollmentCard (CF-only; payload pre-normalized in normalizeCfItems) ───
export function normalizeEnrollmentCardProps(ctx: SlotResolveContext): EnrollmentCardContainerProps {
  const c = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(c);
  return {
    ...c,
    header: c['header'] || '',
    primaryCtaLinkText: c['primaryCtaLinkText'] || '',
    primaryCtaLink: c['primaryCtaLink'] || '',
    primaryCtaOpenInNewTab: c['primaryCtaOpenInNewTab'] === true || c['primaryCtaOpenInNewTab'] === 'true',
    secondaryCtaLink: c['secondaryCtaLink'] || '',
    secondaryCtaLinkText: c['secondaryCtaLinkText'] || '',
    secondaryCtaOpenInNewTab: c['secondaryCtaOpenInNewTab'] === true || c['secondaryCtaOpenInNewTab'] === 'true',
    enrollmentCardDescription: c['enrollmentCardDescription'] || c['description'] || '',
    theme: c['theme'] || 'standard',
    fileReferenceImage: baseUrl,
    altText: c['altText'] || alt,
    trackingProperties: trackingProps(c, ctx.trackingCtx, ctx.slot?.type),
  } as unknown as EnrollmentCardContainerProps;
}

// ─── MerchandisingCoBrandCard ───────────────────────────────────────────────
export function normalizeMerchandisingCoBrandCardProps(ctx: SlotResolveContext): Record<string, unknown> {
  const c = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(c, 'logo');
  const aspectRatio = baseUrl.includes('content/dam') ? undefined : ASPECT_RATIOS.SQUARE;
  const image = buildDynamicMediaNode(baseUrl, aspectRatio, alt) ?? EMPTY_RENDITIONS;

  return {
    ...c,
    headerLong: c['headerLong'] || c['header'] || '',
    headerMedium: c['headerMedium'] || c['header'] || '',
    descriptionLong: c['descriptionLong'] || c['description'] || '',
    descriptionMedium: c['descriptionMedium'] || c['description'] || '',
    callToActionText1: c['callToActionText1'] || c['primaryCTALinkText'] || '',
    destinationUrl1: c['destinationUrl1'] || c['primaryCTALink'] || '',
    callToActionText2: c['callToActionText2'] || c['primaryCTALinkText'] || '',
    destinationUrl2: c['destinationUrl2'] || c['primaryCTALink'] || '',
    openInNewTab: c['openInNewTab'] || c['primaryCtaOpenInNewTab'] || false,
    image,
    trackingProperties: trackingProps(c, ctx.trackingCtx, ctx.slot?.type),
    styleClass: c['theme'] || c['styleclass'] || '',
    isSquare: c['size'] === 'square',
  };
}

// ─── RiottMediaCard ───────────────────────────────────────────────────────
export function normalizeRiottMediaCardProps(ctx: SlotResolveContext): Record<string, unknown> {
  const c = getContentObj(ctx.slot);

  return {
    ...c,
    displayEvergreenContent: false /* Fallback data will be rendered from the Content Fragment in the PDC card. */,
    adData: (c['adData'] as unknown[]) ?? [],
    mboxParameter: c['mboxName'] || c['mboxParameter'],
    isTargetContentLoaded: c['isTargetContentLoaded'] || true,
    isTargetContentError:
      c['isTargetContentError'] ||
      false /* If a DCA response error occurs, RiotMediaCard will not render and will fall back to the Content Fragment. */,
    trackingProperties: trackingProps(c, ctx.trackingCtx, ctx.slot?.type),
    styleclass: `riott-media_border_radius ${c['styleClass'] || c['styleclass'] || ''}`.trim() as string,
    metaData: c['metadata'] || c['metaData'],
  };
}

export function normalizeLayeredFeatureCardProps(ctx: SlotResolveContext) {
  const contentObj = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(contentObj);
  const normalizedContext = {
    slot: ctx.slot,
    contentObj,
    baseUrl,
    alt,
    trackingCtx: ctx.trackingCtx,
  };
  const createProps = pickLayeredFeaturePropsConfiguration(normalizedContext);
  const props = createProps(normalizedContext);

  return props;
}
